"use client";

import { useRouter } from "next/navigation";
import { useCallback } from "react";

type NavigateArgs = { decorateUrl: (url: string) => string };

/**
 * Navigation handed to Clerk's `finalize()` once a session becomes active.
 * Shared by sign-in and sign-up so both land in the same place.
 */
export const useSessionNavigate = ({
  redirectUrl,
  onSignedIn,
}: {
  /** `null` keeps the user on the current page — what the dialog wants. */
  redirectUrl: string | null;
  onSignedIn?: () => void;
}) => {
  const router = useRouter();

  return useCallback(
    ({ decorateUrl }: NavigateArgs) => {
      if (!redirectUrl) {
        onSignedIn?.();
        return;
      }

      const destination = decorateUrl(redirectUrl);
      // A decorated URL can point at Clerk's own domain to refresh the cookie,
      // which client-side navigation cannot handle.
      if (destination.startsWith("http")) {
        window.location.href = destination;
        return;
      }

      router.push(destination);
    },
    [onSignedIn, redirectUrl, router],
  );
};
